import { z } from "zod";
export const ScenarioOperatingModeSchema = z.enum([
    "off",
    "preview",
    "drafts",
    "live",
]);
export const ScenarioOperatingStateSchema = z.object({
    mode: ScenarioOperatingModeSchema,
    scenarioActivationId: z.string().uuid().nullable(),
    scenarioVersionId: z.string().uuid().nullable(),
    revision: z.number().int().nonnegative(),
    changedAt: z.string().datetime().nullable(),
    changedBy: z.string().trim().min(1).max(200).nullable(),
}).strict().superRefine((state, context) => {
    if ((state.scenarioActivationId === null)
        !== (state.scenarioVersionId === null)) {
        context.addIssue({
            code: "custom",
            message: "Scenario operating state requires one exact activation/version pair.",
            path: ["scenarioActivationId"],
        });
    }
    if (state.mode !== "off" && state.scenarioActivationId === null) {
        context.addIssue({
            code: "custom",
            message: "A running scenario mode requires one exact activation.",
            path: ["mode"],
        });
    }
});
export const ScenarioOperatingModeMutationSchema = z.object({
    mode: ScenarioOperatingModeSchema,
    expectedActivationId: z.string().uuid(),
    expectedScenarioVersionId: z.string().uuid(),
    expectedRevision: z.number().int().nonnegative(),
    idempotencyKey: z.string().trim().min(16).max(160),
}).strict();
export const LiveReadyDraftIntakeItemSchema = z.object({
    conversationId: z.string().uuid(),
    draftRevisionId: z.string().uuid(),
    outcome: z.enum([
        "queued",
        "authorized",
        "no_reply_applied",
        "skipped",
        "deferred",
    ]),
    reasonCode: z.string().trim().min(1).max(80).nullable(),
}).strict();
export const LiveReadyDraftIntakeSchema = z.object({
    claimed: z.number().int().nonnegative(),
    queued: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    items: z.array(LiveReadyDraftIntakeItemSchema).max(10_000),
}).strict();
export const ScenarioOperatingModeResultSchema = ScenarioOperatingStateSchema.extend({
    scenarioId: z.string().uuid(),
    replayed: z.boolean(),
    readyDraftIntake: LiveReadyDraftIntakeSchema,
}).strict();
export const AgentRunStateSchema = z.enum([
    "queued",
    "running",
    "waiting",
    "completed",
    "failed",
    "cancelled",
]);
export const AgentRunSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    installationId: z.string().uuid(),
    employeeVersionId: z.string().uuid(),
    scenarioVersionId: z.string().uuid(),
    stageNodeId: z.string().uuid(),
    conversationId: z.string().uuid().nullable(),
    state: AgentRunStateSchema,
    wakeAt: z.string().datetime().nullable(),
    attempt: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
}).strict();
export const MemoryKindSchema = z.enum([
    "relationship_summary",
    "customer_fact",
    "company_knowledge",
    "employee_skill",
    "scenario_progress",
    "correction_example",
    "outcome",
    "improvement_proposal",
]);
export const MemoryRecordSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    installationId: z.string().uuid(),
    kind: MemoryKindSchema,
    employeeId: z.string().uuid().nullable(),
    employeeVersionId: z.string().uuid().nullable(),
    conversationId: z.string().uuid().nullable(),
    contactExternalRef: z.string().trim().min(1).max(500).nullable(),
    sourceType: z.enum([
        "message",
        "human_correction",
        "connector_result",
        "published_knowledge",
        "system_reducer",
    ]),
    sourceId: z.string().uuid().nullable(),
    content: z.string().trim().min(1).max(8_000),
    confidence: z.number().min(0).max(1).nullable(),
    createdAt: z.string().datetime(),
    supersededAt: z.string().datetime().nullable(),
}).strict();
export const HumanCorrectionExampleSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    installationId: z.string().uuid(),
    conversationId: z.string().uuid(),
    employeeVersionId: z.string().uuid().nullable(),
    scenarioVersionId: z.string().uuid().nullable(),
    stageNodeId: z.string().uuid().nullable(),
    originalDraftRevisionId: z.string().uuid(),
    correctedDraftRevisionId: z.string().uuid(),
    originalMessage: z.string().min(1).max(4_000),
    correctedMessage: z.string().min(1).max(4_000),
    reason: z.string().min(5).max(1_000),
    createdBy: z.string().uuid(),
    createdAt: z.string().datetime(),
    outcome: z.string().max(1_000).nullable(),
}).strict();
//# sourceMappingURL=agent-runtime.js.map